import { computed } from 'vue';

/**
 * Resolves the accessible name and `aria-labelledby` target for the trigger,
 * honouring (in order): existing aria-labelledby on the native select,
 * a label[for=id] match, an ancestor <label>, then falling back to the
 * placeholder text.
 *
 * @param {HTMLSelectElement} nativeSelect
 * @param {string} uid - unique component id (used for auto-generated label ids)
 * @param {import('vue').ComputedRef<string>} placeholderText
 */
export function useAccessibleName(nativeSelect, uid, placeholderText) {
    const accessibleName = computed(
        () => nativeSelect.getAttribute('aria-label') || placeholderText.value,
    );

    const labelledBy = computed(() => {
        const existing = nativeSelect.getAttribute('aria-labelledby');
        if (existing) return existing;

        if (nativeSelect.id) {
            const lbl = document.querySelector(
                `label[for="${CSS.escape(nativeSelect.id)}"]`,
            );
            if (lbl) {
                if (!lbl.id) lbl.id = `${nativeSelect.id}-label`;
                return lbl.id;
            }
        }

        const wrap = nativeSelect.closest('label');
        if (wrap) {
            if (!wrap.id) wrap.id = `${uid}-wrap-label`;
            return wrap.id;
        }

        return '';
    });

    return { accessibleName, labelledBy };
}
